// INC2-CONTACT (build order increment 2 C, "One identity"; CRM design §2.1, §2.5, §2.6): the thin
// contact core's routes. No CRM UI beyond a list: the upsert is what the booking, the forms, the
// import and the GHL and HubSpot adapters call; the external-id link is how an adapter attaches
// its ids; consent is append-only.
//
//   GET  contacts                        the list (search by name, email, phone or organisation)
//   GET  contacts/{id}                   one contact with its identities, consent, organisation
//   POST contacts/upsert                 match by the match order, then create or update
//   POST contacts/{id}/external-ids      link an outside system's id (GHL, HubSpot) to it
//   POST contacts/{id}/consent           record consent for a channel (the latest wins)
import { z } from "zod";
import { Contact, ContactConsent, ContactIdentity, Organisation } from "../contact-rows";
import { ConsentBasis, ConsentChannel, ConsentStatus, ContactLifecycle } from "../enums";
import { LIMITS, Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams, PageQuery, page } from "./common";
import { defineRoute } from "./route";

const co = "/v1/companies/{co}";
const core = { feature: "core", tag: "contacts" } as const;

/** Who an upsert says it came from (`manual` is a person's own entry). */
export const ContactUpsertSource = z.enum(["manual", "agent", "booking", "form", "import", "sync"]);

/** An id the contact has in an outside system (GHL contact id, HubSpot object id). */
export const ContactExternalRef = z
  .strictObject({
    /** `ghl`, `hubspot`, … (lower-case key). */
    provider: z
      .string()
      .min(2)
      .max(40)
      .regex(/^[a-z][a-z0-9_]*$/, "a lower-case provider key"),
    /** The account in it (GHL location id, HubSpot portal id); absent when it has none. */
    account: z.string().trim().min(1).max(200).optional(),
    /** The object type there (`contact`). */
    object: z
      .string()
      .min(1)
      .max(80)
      .regex(/^[a-z][a-z0-9_]*$/, "a lower-case object key")
      .default("contact"),
    external_id: z.string().trim().min(1).max(320),
    /** The connection it came through (this company's). */
    connection_id: Uuid.optional(),
  })
  .meta({ id: "ContactExternalRef" });
export type ContactExternalRef = z.infer<typeof ContactExternalRef>;

export const ContactConsentInput = z
  .strictObject({
    channel: ConsentChannel,
    status: ConsentStatus,
    basis: ConsentBasis,
    evidence: z.string().trim().min(1).max(LIMITS.text).optional(),
  })
  .meta({ id: "ContactConsentInput" });

export const ContactUpsert = z
  .strictObject({
    email: z.string().trim().min(3).max(254).optional(),
    phone: z.string().trim().min(3).max(40).optional(),
    external: ContactExternalRef.optional(),
    first_name: z.string().trim().max(LIMITS.name).optional(),
    last_name: z.string().trim().max(LIMITS.name).optional(),
    /** Absent: from the names, else the email, else the phone. */
    display_name: z.string().trim().max(LIMITS.name).optional(),
    title: z.string().trim().max(LIMITS.name).optional(),
    timezone: z.string().trim().max(64).optional(),
    lifecycle: ContactLifecycle.optional(),
    organisation: z
      .strictObject({
        name: z.string().trim().min(1).max(LIMITS.name).optional(),
        domain: z.string().trim().min(3).max(253).optional(),
      })
      .optional(),
    source: ContactUpsertSource.default("manual"),
    /** What the source names it by (a booking id, a form URL); never a message body. */
    source_ref: z.string().trim().min(1).max(500).optional(),
    consent: z.array(ContactConsentInput).max(5).optional(),
  })
  .meta({ id: "ContactUpsert" });
export type ContactUpsert = z.infer<typeof ContactUpsert>;

/** A list row: the contact with its primary email and phone and its organisation's name. */
export const ContactListItem = Contact.extend({
  email: z.string().nullable(),
  phone: z.string().nullable(),
  organisation_name: z.string().nullable(),
}).meta({ id: "ContactListItem" });
export type ContactListItem = z.infer<typeof ContactListItem>;

export const ContactDetail = Contact.extend({
  identities: z.array(ContactIdentity),
  /** The latest consent per channel (the only one that counts). */
  consent: z.array(ContactConsent),
  organisation: Organisation.nullable(),
  /** Open duplicate candidates naming this contact. */
  duplicate_ids: z.array(Uuid),
}).meta({ id: "ContactDetail" });
export type ContactDetail = z.infer<typeof ContactDetail>;

export const ContactUpsertResult = z
  .object({
    /** `new`: created; `existing`: one contact matched; `candidate`: two matched (one kept). */
    outcome: z.enum(["new", "existing", "candidate"]),
    contact: ContactDetail,
    /** `candidate`: the other contacts an identity of the input belongs to. */
    candidate_ids: z.array(Uuid),
  })
  .meta({ id: "ContactUpsertResult" });
export type ContactUpsertResult = z.infer<typeof ContactUpsertResult>;

export const ContactListQuery = PageQuery.extend({
  /** Name, email, phone or organisation name contains this. */
  q: z.string().trim().max(LIMITS.name).optional(),
  archived: z.enum(["true", "false"]).optional(),
});

export const contactRoutes = {
  "contacts.list": defineRoute({
    ...core,
    id: "contacts.list",
    method: "get",
    path: `${co}/contacts`,
    summary: "Contacts: people outside the company, newest first",
    access: "member",
    params: CompanyParams,
    query: ContactListQuery,
    response: { status: 200, schema: page(ContactListItem) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "contacts.get": defineRoute({
    ...core,
    id: "contacts.get",
    method: "get",
    path: `${co}/contacts/{id}`,
    summary: "One contact with its identities, latest consent per channel and organisation",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: ContactDetail },
    errors: [],
    emits: [],
  }),
  "contacts.upsert": defineRoute({
    ...core,
    id: "contacts.upsert",
    method: "post",
    path: `${co}/contacts/upsert`,
    summary:
      "Find a contact by its identities (external id, then email, then phone) and update it, or create it",
    access: "member",
    params: CompanyParams,
    body: ContactUpsert,
    idempotency: "optional",
    response: { status: 200, schema: ContactUpsertResult },
    errors: ["contact_identity_taken"],
    emits: ["contact.upserted", "contact.identity_linked", "contact.consent_recorded"],
  }),
  "contacts.linkExternal": defineRoute({
    ...core,
    id: "contacts.linkExternal",
    method: "post",
    path: `${co}/contacts/{id}/external-ids`,
    summary: "Link an outside system's id (GHL, HubSpot) to a contact",
    access: "member",
    params: CompanyItemParams,
    body: ContactExternalRef,
    idempotency: "optional",
    response: { status: 201, schema: ContactIdentity },
    errors: ["contact_identity_taken"],
    emits: ["contact.identity_linked"],
  }),
  "contacts.recordConsent": defineRoute({
    ...core,
    id: "contacts.recordConsent",
    method: "post",
    path: `${co}/contacts/{id}/consent`,
    summary: "Record a contact's consent for a channel (the latest per channel wins)",
    access: "member",
    params: CompanyItemParams,
    body: ContactConsentInput,
    idempotency: "optional",
    response: { status: 201, schema: ContactConsent },
    errors: ["consent_opt_out_locked"],
    emits: ["contact.consent_recorded"],
  }),
} as const;
